/**
 * The collapse interaction shared by `JsonKeyValueTable` (one payload) and
 * `JsonDiffTable` (two payloads compared).
 *
 * What is shared is deliberately NOT a "tree table" component. The two build
 * their rows from different walks — one value versus two aligned side by side,
 * with key unions and array index alignment — and merging those walks would
 * grow a string of `if (hasSecondSide)` branches. What they genuinely have in
 * common is the row MODEL: a depth-annotated list in DFS order. On that model,
 * folding is "skip the following rows deeper than this one", which is the same
 * six lines for both.
 *
 * The two differ in exactly one behaviour, and it is a domain difference rather
 * than drift: a diff knows which subtrees changed and opens those, while a lone
 * payload knows nothing and opens everything. Callers say which via
 * `isDefaultCollapsed`.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { cn } from '../../cn';

/** Indentation per nesting level, shared so the two tables line up identically. */
export const INDENT_UNIT = 16;

/** The minimum a row must carry for folding to work. */
export interface CollapsibleRow {
  /** Unique within one table, and the identity folding is remembered by. */
  path: string;
  depth: number;
}

export interface CollapsibleTree<R extends CollapsibleRow> {
  /** Rows to render, with folded subtrees removed. */
  visible: readonly R[];
  isCollapsed: (row: R) => boolean;
  /**
   * Whether a row has anything to fold.
   *
   * Decided by "does a deeper row follow this one", NOT by "is this value a
   * container": an empty array is a container with zero children, and giving
   * it a twisty produces a control claiming `aria-expanded` over nothing.
   */
  hasChildren: (row: R) => boolean;
  toggle: (row: R) => void;
}

export function useCollapsibleTree<R extends CollapsibleRow>(
  rows: readonly R[],
  isDefaultCollapsed: (row: R) => boolean,
): CollapsibleTree<R> {
  // Only rows the operator actually clicked live here. Everything else follows
  // the caller's default, so a payload that re-renders with new data does not
  // carry over stale open/closed state for paths that no longer mean the same.
  const [overrides, setOverrides] = useState<ReadonlyMap<string, boolean>>(() => new Map());

  const childBearing = useMemo(() => {
    const withChildren = new Set<string>();
    for (let index = 0; index < rows.length - 1; index += 1) {
      if (rows[index + 1].depth > rows[index].depth) withChildren.add(rows[index].path);
    }
    return withChildren;
  }, [rows]);

  const isCollapsed = useCallback(
    (row: R) => overrides.get(row.path) ?? isDefaultCollapsed(row),
    [overrides, isDefaultCollapsed],
  );

  const hasChildren = useCallback((row: R) => childBearing.has(row.path), [childBearing]);

  const visible = useMemo(() => {
    const out: R[] = [];
    // `null` while not skipping; otherwise the depth of the folded ancestor,
    // and every row deeper than it belongs to the subtree being hidden.
    let foldedAt: number | null = null;
    for (const row of rows) {
      if (foldedAt !== null) {
        if (row.depth > foldedAt) continue;
        foldedAt = null;
      }
      out.push(row);
      if (childBearing.has(row.path) && isCollapsed(row)) foldedAt = row.depth;
    }
    return out;
  }, [rows, childBearing, isCollapsed]);

  const toggle = useCallback(
    (row: R) => {
      setOverrides((current) => {
        const next = new Map(current);
        next.set(row.path, !(current.get(row.path) ?? isDefaultCollapsed(row)));
        return next;
      });
    },
    [isDefaultCollapsed],
  );

  return { visible, isCollapsed, hasChildren, toggle };
}

/**
 * The twisty for a container row.
 *
 * It carries the keyboard semantics — it is the focusable, `aria-expanded`
 * control — but it deliberately has NO click handler of its own. The whole row
 * is the click target, and this button's click bubbles up to it. Handlers on
 * both would toggle twice: a no-op that looks exactly like a dead button, and
 * one that no assertion on the row's own click would ever catch.
 */
export function TreeTwisty({ expanded, label }: { expanded: boolean; label: string }) {
  return (
    <button
      type="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={label}
      className="mr-1 inline-flex h-3 w-3 shrink-0 items-center justify-center rounded-sm text-muted-foreground/70 transition-transform hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <svg viewBox="0 0 8 8" className="h-2 w-2 fill-current" aria-hidden="true">
        {expanded ? <path d="M0 2h8L4 7z" /> : <path d="M2 0v8l5-4z" />}
      </svg>
    </button>
  );
}

/**
 * The indented key cell both tables use, so nesting reads the same in each.
 *
 * `spacer` keeps a leaf's label aligned with its siblings that do have a
 * twisty; without it every container row would sit 12px left of its own
 * children's labels.
 */
export function TreeKeyCell({
  depth,
  expanded,
  hasChildren,
  label,
  indentPx = INDENT_UNIT,
  className,
  children,
}: {
  depth: number;
  expanded: boolean;
  hasChildren: boolean;
  /** Accessible name for the twisty; the visible label is `children`. */
  label: string;
  indentPx?: number;
  className?: string;
  children: ReactNode;
}) {
  return (
    <td
      className={cn(
        'w-1/3 min-w-[160px] max-w-[320px] border-r border-border/60 py-1.5 pr-3 align-top font-mono text-[11px] text-muted-foreground',
        className,
      )}
      style={{ paddingLeft: `${8 + depth * indentPx}px` }}
    >
      {hasChildren ? (
        <TreeTwisty expanded={expanded} label={label} />
      ) : (
        <span className="mr-1 inline-block h-3 w-3 shrink-0" aria-hidden="true" />
      )}
      {children}
    </td>
  );
}

/** Row-level affordances for a container row: the whole row is the target. */
export const TREE_NODE_ROW_CLASS =
  'cursor-pointer bg-muted/40 hover:bg-muted/70 focus-within:bg-muted/70';
